import { getSession } from "@masterkey/supabase/queriesClient";

export interface Session {
  accessToken: string;
  user: {
    id: string;
    email: string;
  };
}

export async function auth(): Promise<Session | null> {
  try {
    const {
      data: { session },
      error,
    } = await getSession();

    if (error || !session) {
      return null;
    }

    return {
      accessToken: session.access_token,
      user: {
        id: session.user.id,
        email: session.user.email ?? "",
      },
    };
  } catch (error) {
    console.error("Auth error:", error);
    return null;
  }
}

// Helper function to get the current session token
export async function getAuthToken(): Promise<string | null> {
  const {
    data: { session },
  } = await getSession();
  return session?.access_token ?? null;
}

/**
 * Helper function for authenticated API calls.
 * If no auth token is present, redirects to login page with current path as return URL.
 */
export async function authenticatedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const token = await getAuthToken();

  if (!token) {
    return redirectToLogin();
  }

  const headers = new Headers(init?.headers);
  headers.set("Authorization", `Bearer ${token}`);

  return fetch(input, {
    ...init,
    headers,
  });
}

export async function publicFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
) {
  return fetch(input, init);
}

/**
 * Redirects to the login page with the current path as the "from" parameter
 * @returns Promise that never resolves, as we redirect the browser
 */
export function redirectToLogin(): Promise<never> {
  const currentPath = window.location.pathname + window.location.search;
  const loginUrl = new URL("/login", window.location.origin);
  loginUrl.searchParams.set("from", currentPath);
  window.location.href = loginUrl.toString();
  // Return a promise that never resolves
  return new Promise(() => {});
}
